import { supabase } from '../supabase';

// ── Athletes ─────────────────────────────────────────────────────────────

/** The only live athlete-creation path in V0.1A — creates the athlete row
 * and the caller's guardian relationship atomically, server-side.
 *
 * stateCode/countyId/schoolDistrictId/zipCode are the school/home-program
 * context added in 011 — coarse geographic context only, never used for
 * authorization and never exposed publicly. County/district are
 * canonical references (011 replaced the earlier free-text draft with
 * real FKs into counties/school_districts), not names — the UI's
 * dropdowns already only ever produce a valid id or null. All optional. */
export async function createAthlete({
  firstName, lastName, dateOfBirth, grade, heightIn, weightLb,
  stateCode, countyId, schoolDistrictId, zipCode,
}) {
  const { data, error } = await supabase.rpc('create_athlete_with_guardian', {
    p_first_name: firstName,
    p_last_name: lastName,
    p_date_of_birth: dateOfBirth,
    p_grade: grade,
    p_height_in: heightIn || null,
    p_weight_lb: weightLb || null,
    p_state_code: stateCode || null,
    p_county_id: countyId || null,
    p_school_district_id: schoolDistrictId || null,
    p_zip_code: zipCode || null,
  });
  if (error) throw error;
  return data;
}

/** Active athletes this guardian actually MANAGES (019) — an active
 * guardian_athletes relationship, not merely whatever the athletes
 * table's broader SELECT RLS would additionally let this session read
 * (team-staff or platform-admin visibility). Powers both My Athletes and
 * every selector built on top of this function (e.g. the invite flow's
 * "Choose an existing athlete" dropdown) — a guardian must never be
 * offered an athlete they don't manage as a candidate for a roster
 * request, even as a display convenience. The archive-state filter
 * itself remains display-convenience only, same as before 019 —
 * archive_athlete()/restore_athlete() and request_team_roster_join()
 * independently re-enforce both ownership and archive state
 * server-side regardless of what this query returns. */
export async function listMyAthletes() {
  const { data, error } = await supabase
    .rpc('list_my_managed_athletes', { p_status: 'active' })
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data;
}

/** Archived athletes this guardian manages (019), most recently archived
 * first — for the "View Archived Athletes" list. Same managed-only
 * scoping as listMyAthletes() above, just the other status value. */
export async function listArchivedAthletes() {
  const { data, error } = await supabase
    .rpc('list_my_managed_athletes', { p_status: 'archived' })
    .order('archived_at', { ascending: false });
  if (error) throw error;
  return data;
}

/** Marks an athlete inactive on FFN — never a delete. Preserves every
 * historical record (measurements, testing sessions, guardian
 * relationships, team membership) untouched; also cancels that
 * athlete's own pending roster requests server-side (018). Authorized
 * server-side against an active guardian_athletes relationship (or
 * platform admin) — this call cannot succeed for an athlete the caller
 * doesn't actually manage, regardless of what athleteId is passed. */
export async function archiveAthlete(athleteId) {
  const { data, error } = await supabase.rpc('archive_athlete', { p_athlete_id: athleteId });
  if (error) throw error;
  return data;
}

/** Restores the SAME athlete row — status back to active, archived_at/
 * archived_by cleared. Never creates a new athlete, never touches
 * team_athletes (018) — whatever roster history existed is unaffected
 * in both directions. Same server-side authorization as archiveAthlete. */
export async function restoreAthlete(athleteId) {
  const { data, error } = await supabase.rpc('restore_athlete', { p_athlete_id: athleteId });
  if (error) throw error;
  return data;
}

/** Embeds the canonical county/school-district NAMES via their FK
 * relationships (011) — athletes only stores county_id/school_district_id,
 * never a duplicated name, so anything that wants to display "Jefferson"
 * or "Fort Atkinson" reads it through this join, not from a column on
 * athletes itself. Aliased to county/school_district so callers don't
 * need to know the underlying reference-table names. */
export async function getAthlete(athleteId) {
  const { data, error } = await supabase
    .from('athletes')
    .select('*, county:counties(name), school_district:school_districts(name)')
    .eq('id', athleteId)
    .single();
  if (error) throw error;
  return data;
}

export async function updateAthlete(athleteId, fields) {
  const { data, error } = await supabase
    .from('athletes')
    .update(fields)
    .eq('id', athleteId)
    .select()
    .single();
  if (error) throw error;
  return data;
}

// ── Growth measurements ──────────────────────────────────────────────────
// athletes.height_in/weight_lb are cached CURRENT values only — the
// authoritative source is athlete_measurements. getAthlete() above already
// returns the current cached values (kept in sync server-side after every
// write here), so most of the app just keeps reading athlete.height_in/
// weight_lb as before; these functions are for the dated history and for
// adding/correcting a measurement.

/** Normal history — excludes soft-deleted rows. RLS already hides deleted
 * rows from guardians, but a platform admin's policy deliberately does
 * NOT filter them out (so support access can see everything) — this
 * explicit filter is what keeps deleted rows out of the ordinary Athlete
 * Profile UI regardless of who's viewing it. */
export async function listMeasurements(athleteId) {
  const { data, error } = await supabase
    .from('athlete_measurements')
    .select('*')
    .eq('athlete_id', athleteId)
    .is('deleted_at', null)
    .order('measurement_date', { ascending: false })
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data;
}

/** One active row per athlete/date/gear-status, enforced server-side. If
 * an active record already exists for that exact combination, this merges
 * into it — a supplied null never erases the other field's existing
 * value — instead of creating a second row. A no_gear and a with_gear
 * reading on the same date are different rows by design; weightGearStatus
 * defaults to 'no_gear' when not specified. A with_gear entry MAY include
 * heightIn (weight_gear_status describes the weight only, not the whole
 * row) — but only one active row per athlete/date may carry a non-null
 * height across ALL gear statuses combined; a second one is rejected
 * server-side with a clean error, surfaced via e.message. */
export async function addMeasurement({ athleteId, measurementDate, heightIn, weightLb, weightGearStatus }) {
  const { data, error } = await supabase.rpc('add_athlete_measurement', {
    p_athlete_id: athleteId,
    p_measurement_date: measurementDate,
    p_height_in: heightIn || null,
    p_weight_lb: weightLb || null,
    p_weight_gear_status: weightGearStatus || 'no_gear',
  });
  if (error) throw error;
  return data;
}

/** weightGearStatus may also be corrected here (e.g. relabeling a legacy
 * 'unknown' row). If the correction would collide with another active row
 * for the resulting date/gear-status, the RPC raises a clear message
 * (e.g. "A With Gear measurement already exists...") rather than a raw
 * database error — surface e.message to the user as-is. */
export async function updateMeasurement(measurementId, { measurementDate, heightIn, weightLb, weightGearStatus }) {
  const { data, error } = await supabase.rpc('update_athlete_measurement', {
    p_measurement_id: measurementId,
    p_measurement_date: measurementDate,
    p_height_in: heightIn || null,
    p_weight_lb: weightLb || null,
    p_weight_gear_status: weightGearStatus || null,
  });
  if (error) throw error;
  return data;
}

/** Soft delete only — never a physical remove. The athlete's current
 * height/weight cache falls back automatically (server-side) to the next
 * most recent active measurement, independently per field. */
export async function removeMeasurement(measurementId) {
  const { data, error } = await supabase.rpc('remove_athlete_measurement', {
    p_measurement_id: measurementId,
  });
  if (error) throw error;
  return data;
}

// ── Equipment / gear-weight history ─────────────────────────────────────
// A dated history of equipment load, entirely separate from
// athlete_measurements — an athlete's gear weight changes during a
// season, so each change is its own new dated fact, never an overwrite
// of the last one. Used only to derive a display-time "Estimated Body
// Weight" for a with_gear measurement (see gearWeightUtils.js) — it
// never writes to weight_lb and never feeds athletes.weight_lb / Current
// Body Weight.

/** Normal history — excludes soft-deleted rows, same convention as
 * listMeasurements. */
export async function listGearWeights(athleteId) {
  const { data, error } = await supabase
    .from('athlete_gear_weights')
    .select('*')
    .eq('athlete_id', athleteId)
    .is('deleted_at', null)
    .order('effective_date', { ascending: false })
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data;
}

/** One active row per athlete/date, enforced server-side. A re-add for a
 * date that already has an active record overwrites that day's gear
 * weight outright (gear_weight_lb is always required input, unlike a
 * measurement's height/weight); a blank note preserves whatever note is
 * already there instead of erasing it. */
export async function addGearWeight({ athleteId, effectiveDate, gearWeightLb, notes }) {
  const { data, error } = await supabase.rpc('add_athlete_gear_weight', {
    p_athlete_id: athleteId,
    p_effective_date: effectiveDate,
    p_gear_weight_lb: gearWeightLb,
    p_notes: notes || null,
  });
  if (error) throw error;
  return data;
}

/** If the correction would collide with another active row for the
 * resulting date, the RPC raises a clear message rather than a raw
 * database error — surface e.message to the user as-is. */
export async function updateGearWeight(gearWeightId, { effectiveDate, gearWeightLb, notes }) {
  const { data, error } = await supabase.rpc('update_athlete_gear_weight', {
    p_gear_weight_id: gearWeightId,
    p_effective_date: effectiveDate,
    p_gear_weight_lb: gearWeightLb,
    p_notes: notes || null,
  });
  if (error) throw error;
  return data;
}

/** Soft delete only — never a physical remove. */
export async function removeGearWeight(gearWeightId) {
  const { data, error } = await supabase.rpc('remove_athlete_gear_weight', {
    p_gear_weight_id: gearWeightId,
  });
  if (error) throw error;
  return data;
}

// ── Test type metadata ──────────────────────────────────────────────────

export async function fetchTestTypes() {
  const { data, error } = await supabase
    .from('test_types')
    .select('*')
    .eq('active', true)
    .order('sort_order');
  if (error) throw error;
  return data;
}

// ── Testing sessions + attempts ─────────────────────────────────────────

export async function listTestingSessions(athleteId) {
  const { data, error } = await supabase
    .from('testing_sessions')
    .select('*, test_attempts(*)')
    .eq('athlete_id', athleteId)
    .order('test_date', { ascending: false });
  if (error) throw error;
  return data;
}

export async function getTestingSession(sessionId) {
  const { data, error } = await supabase
    .from('testing_sessions')
    .select('*, test_attempts(*)')
    .eq('id', sessionId)
    .single();
  if (error) throw error;
  return data;
}

/** attemptsByType: { forty_yard: [5.6, 5.59, 5.61], broad_jump: [96, 98, null] }
 * Blank/null slots are dropped, not saved as zero.
 *
 * Both create and update go through SECURITY DEFINER RPCs that write the
 * session and every attempt inside a single Postgres transaction — see
 * migration 005 (create_testing_session_with_attempts /
 * update_testing_session_with_attempts). There is no separate client-side
 * session-then-attempts sequence anymore: either the whole session saves
 * with all its attempts, or nothing changes at all. */
export async function createTestingSession({ athleteId, testDate, attemptsByType }) {
  const { data, error } = await supabase.rpc('create_testing_session_with_attempts', {
    p_athlete_id: athleteId,
    p_test_date: testDate,
    p_attempts: attemptsByType,
  });
  if (error) throw error;
  return data;
}

export async function updateTestingSession(sessionId, { testDate, attemptsByType }) {
  const { data, error } = await supabase.rpc('update_testing_session_with_attempts', {
    p_session_id: sessionId,
    p_test_date: testDate,
    p_attempts: attemptsByType,
  });
  if (error) throw error;
  return data;
}
